import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { RefreshCw, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Dashboard review card (Josh 07/24): payments that landed on a refinanced
 * (closed) deal after its payoff — the bank's uncancellable pre-originated
 * pull. One-click re-route to the successor deal, or Keep (intentional
 * overpull). Non-blocking: renders nothing when there's nothing to review.
 */
const RefiPaymentReview = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/refi-payment-review`, { headers: getAuthHeaders() });
      setItems(res.data.items || []);
    } catch (e) { /* silent — dashboard card is best-effort */ }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const move = async (item) => {
    if (!item.successor) return;
    setBusy(b => ({ ...b, [item.transaction_id]: true }));
    try {
      await axios.post(`${API}/transactions/bulk-move`, [item.transaction_id], {
        params: { target_deal_id: item.successor.id },
        headers: getAuthHeaders()
      });
      toast.success(`Moved ${fmt$(item.amount)} to deal #${item.successor.deal_number}`);
      setItems(prev => prev.filter(i => i.transaction_id !== item.transaction_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move payment');
    } finally {
      setBusy(b => ({ ...b, [item.transaction_id]: false }));
    }
  };

  const keep = async (item) => {
    setBusy(b => ({ ...b, [item.transaction_id]: true }));
    try {
      await axios.post(`${API}/refi-payment-review/keep`, { transaction_id: item.transaction_id }, { headers: getAuthHeaders() });
      toast.success('Kept on the original deal');
      setItems(prev => prev.filter(i => i.transaction_id !== item.transaction_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setBusy(b => ({ ...b, [item.transaction_id]: false }));
    }
  };

  if (items.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/50 mb-6" data-testid="refi-payment-review-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-amber-900 text-base flex items-center gap-2">
            Refi Payments to Review
            <span className="text-xs font-semibold bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">{items.length}</span>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2 text-amber-700">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <p className="text-xs text-amber-700">Payments that landed on a refinanced (closed) deal after its payoff — likely the bank's pre-originated pull.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map(item => (
          <div key={item.transaction_id} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-amber-200 px-3 py-2 text-sm" data-testid={`refi-review-row-${item.transaction_id}`}>
            <div className="flex-1 min-w-0">
              <span className="font-semibold">{fmt$(item.amount)}</span>
              <span className="text-slate-500"> on </span>
              <Link to={`/deals/${item.old_deal.id}`} className="font-medium text-blue-700 hover:underline">
                #{item.old_deal.deal_number} {item.old_deal.shortcode ? `(${item.old_deal.shortcode})` : ''}
              </Link>
              <span className="text-slate-500"> {item.date}</span>
              {item.successor && (
                <span className="text-slate-500"> — refinanced into <span className="font-medium text-slate-700">#{item.successor.deal_number}</span></span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {item.successor ? (
                <Button size="sm" disabled={busy[item.transaction_id]}
                  className="h-7 px-2.5 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => move(item)} data-testid={`refi-review-move-${item.transaction_id}`}>
                  <ArrowRight className="h-3 w-3 mr-1" />Move to #{item.successor.deal_number}
                </Button>
              ) : (
                <span className="text-xs text-slate-400">no successor found</span>
              )}
              <Button size="sm" variant="outline" disabled={busy[item.transaction_id]}
                className="h-7 px-2.5 text-xs"
                onClick={() => keep(item)} data-testid={`refi-review-keep-${item.transaction_id}`}>
                Keep here
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default RefiPaymentReview;
